import { ButtonLink, ExternalLink, Right } from '@edgebox/react-components';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { ProcessTask, ProcessTaskList, ProcessTaskStep, ProcessTaskStepDescription } from '../../Shared/UserTasks';
import { ClientProjectEntity } from '@edgebox/api-rest-client';
import ProjectFormNewCustomer from '../../../common/components/Syndication/Forms/ProjectFormNewCustomer';
import { TaskStatusIcon } from './Onboarding';
import { AppContext } from '../../../common/contexts/AppContext';
import { ApiContext } from '../../../common';
import { useNavigate } from 'react-router';
import { TaskStatusList } from './OnboardingDefinitions';
import {
  CONTENT_CLOUD_DEFAULT_TASK_STATUS,
  CONTENT_CLOUD_ONBOARDING_TASK_NAMES,
  CONTENT_CLOUD_TASK_CONNECT_SOURCE,
  CONTENT_CLOUD_TASK_CONTENT_USER_DATA,
  CONTENT_CLOUD_TASK_CREATE_SPACE,
  CONTENT_CLOUD_TASK_EXPLORE,
  CONTENT_CLOUD_TASK_INTEGRATE,
  CONTENT_CLOUD_TASK_NEXT_STEPS,
  ContentCloudOnboardingTask,
} from './ContentCloudOnboarding';
import { SyncCoreSharedProjectLinkEntity } from '@edgebox/sync-core-data-definitions';
import { ContentCloudComponentDataLoader, initContentCloudAndRetry, updateAccessToken } from '../ContentCloud/WithContentCloud';
import { PublisherClient } from '../ContentCloud/PublisherClient';
import { ContentCloudPermission } from '../ContentCloud/shared-permissions';
import {
  CONTENT_CLOUD_API_VERSION,
  getContentCloudSatelliteUrl,
  isContentUserDataType,
  loadAllContentTypes,
} from '../ContentCloud/content-cloud-helper';
import { ContentCloudSystemRestClient } from '../ContentCloud/ContentCloudSystemRestClient';
import { Button } from 'react-bootstrap';
import { AddProjectLinkForm } from '../../../common/components/Syndication/Forms/ProjectLinkForm';
import { PrimaryActionButton, PrimaryActionLinkButton } from '../../Shared/PrimaryActionButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLinkedin } from '@fortawesome/free-brands-svg-icons/faLinkedin';

const FEEDBACK_URL = 'https://www.content-sync.io/contact/?topic=feedback-content-cloud';

export function ContentCloudOnboardingInstructions({ forceNew }: { forceNew?: boolean }) {
  const app = useContext(AppContext);
  const api = useContext(ApiContext);

  const [project, setProject] = useState<ClientProjectEntity | null>(null);

  const [projectLinks, setProjectLinks] = useState<SyncCoreSharedProjectLinkEntity[] | null>(null);
  const [projectLinkCount, setProjectLinkCount] = useState<number | null>(null);
  const [contentTypeCount, setContentTypeCount] = useState<number | null>(null);
  const [contentCount, setContentCount] = useState<number | null>(null);
  const [exploredRestApi, setExploredRestApi] = useState<boolean | null>(null);
  const [exploredGraphQlApi, setExploredGraphQlApi] = useState<boolean | null>(null);
  const [clientCount, setClientCount] = useState<number | null>(null);
  const [generatedClient, setGeneratedClient] = useState<boolean | null>(null);
  const [usedClient, setUsedClient] = useState<boolean | null>(null);
  const [ignoreClients, setIgnoreClients] = useState<boolean | null>(null);
  const [contentUserDataTypeCount, setContentUserDataTypeCount] = useState<number | null>(null);

  const [hadUserInteraction, setHadUserInteraction] = useState(false);
  const [activeTaskGroup, setActiveTaskGroup] = useState<null | string>(null);

  const [taskStatus, setTaskStatus] = useState<TaskStatusList<ContentCloudOnboardingTask>>({ ...CONTENT_CLOUD_DEFAULT_TASK_STATUS });
  const setDone = useMemo(
    () => (task: ContentCloudOnboardingTask, done: boolean) => {
      if (taskStatus[task] === done) {
        return;
      }

      const taskStatusList = {
        ...taskStatus,
        [task]: done,
      };
      setTaskStatus(taskStatusList);

      if (hadUserInteraction) {
        return;
      }

      for (let i = CONTENT_CLOUD_ONBOARDING_TASK_NAMES.length - 1; i >= 0; i--) {
        const task = CONTENT_CLOUD_ONBOARDING_TASK_NAMES[i];
        if (taskStatusList[task] === 'loading') {
          return;
        }
        const previousTask = CONTENT_CLOUD_ONBOARDING_TASK_NAMES[i === CONTENT_CLOUD_ONBOARDING_TASK_NAMES.length - 1 ? i : i + 1];
        if (taskStatusList[task] === true) {
          setActiveTaskGroup(previousTask);
          break;
        }
      }
    },
    [taskStatus, hadUserInteraction]
  );
  const navigate = useNavigate();
  useEffect(() => {
    setDone('create-space', !!project);

    if (project) {
      navigate(`/projects/${project.id}/onboarding`);
    }
  }, [project, setDone, navigate]);
  useEffect(() => {
    if (projectLinkCount !== null && contentTypeCount !== null && contentCount !== null) {
      setDone('connect-source', !!projectLinkCount && !!contentTypeCount && !!contentCount);
    }
  }, [setDone, projectLinkCount, contentTypeCount, contentCount]);
  useEffect(
    () =>
      clientCount !== null && generatedClient !== null && ignoreClients !== null
        ? setDone('integrate', (!!clientCount || !!ignoreClients) && !!generatedClient)
        : void 0,
    [clientCount, ignoreClients, generatedClient, setDone]
  );
  useEffect(
    () =>
      exploredRestApi !== null && exploredGraphQlApi !== null && clientCount !== null && ignoreClients !== null
        ? setDone('explore', (!!clientCount || !!ignoreClients) && !!exploredRestApi && !!exploredGraphQlApi)
        : void 0,
    [clientCount, ignoreClients, exploredRestApi, exploredGraphQlApi, setDone]
  );
  useEffect(
    () => (contentUserDataTypeCount !== null ? setDone('content-user-data', !!contentUserDataTypeCount) : void 0),
    [contentUserDataTypeCount, setDone]
  );

  const [contentCloudData, setContentCloudData] = useState<ContentCloudComponentDataLoader | null>(null);
  const [environmentAccessToken, setEnvironmentAccessToken] = useState<string | null>(null);
  useEffect(() => {
    if (!app.customer || !project || !api || !project.syncCore) {
      return;
    }

    project.syncCore?.get().then(async (contentCloud) => {
      if (!contentCloud) {
        return;
      }

      const data = await initContentCloudAndRetry(
        api,
        contentCloud,
        [ContentCloudPermission.SPACE_READ, ContentCloudPermission.CLIENT_READ],
        project
      );
      setContentCloudData(data);
      data.onLoad((contentCloudData) =>
        setContentCloudData({
          ...data,
          contentCloudData,
        })
      );
    });
  }, [project, api, app.customer]);
  useEffect(() => {
    if (!api || !contentCloudData?.contentCloudData) {
      return;
    }

    updateAccessToken(api, contentCloudData.contentCloudData, [
      ContentCloudPermission.SPACE_READ,
      ContentCloudPermission.CONTENT_READ,
      ContentCloudPermission.CONTENT_TYPE_READ,
    ]).then((environmentAccessToken) => setEnvironmentAccessToken(environmentAccessToken));
  }, [api, contentCloudData?.contentCloudData]);

  useEffect(() => {
    if (!environmentAccessToken || !contentCloudData?.contentCloudData) {
      return;
    }

    const publisherClient = new PublisherClient({
      accessToken: contentCloudData.contentCloudData.accessToken,
      baseUrl: contentCloudData.contentCloudData.contentCloud.baseUrl,
      version: CONTENT_CLOUD_API_VERSION,
    });
    publisherClient.getClients().then((clients) => setClientCount(clients.length));

    const restClient = new ContentCloudSystemRestClient({
      accessToken: environmentAccessToken,
      baseUrl: getContentCloudSatelliteUrl(contentCloudData.contentCloudData.contentCloud, {
        api: 'rest',
        service: 'live',
        environmentSubdomain: `${contentCloudData.contentCloudData.space?.domainKey}-${contentCloudData.contentCloudData.environment?.domainKey}`,
        version: CONTENT_CLOUD_API_VERSION,
      }),
    });
    loadAllContentTypes(restClient).then((contentTypes) => {
      setContentTypeCount(contentTypes.filter((c) => !isContentUserDataType(c)).length);
      setContentUserDataTypeCount(contentTypes.filter((c) => isContentUserDataType(c)).length);
    });
    restClient.contentCollection({ limit: 1 }).then((content) => setContentCount(content.total));
  }, [environmentAccessToken, contentCloudData?.contentCloudData]);
  useEffect(() => {
    if (!project) {
      return;
    }

    setExploredRestApi(localStorage.getItem(`content-sync/onboarding/content-cloud/${project.id}/explored-rest-api`) === 'true');
    setExploredGraphQlApi(localStorage.getItem(`content-sync/onboarding/content-cloud/${project.id}/explored-graphql-api`) === 'true');
    setGeneratedClient(localStorage.getItem(`content-sync/onboarding/content-cloud/${project.id}/generated-client`) === 'true');
    setUsedClient(localStorage.getItem(`content-sync/onboarding/content-cloud/${project.id}/used-client`) === 'true');
    setIgnoreClients(localStorage.getItem(`content-sync/onboarding/content-cloud/${project.id}/ignore-clients`) === 'true');
  }, [project, setDone]);
  useEffect(() => {
    if (!project || !api) {
      return;
    }

    api.syndication.projectLinks.search({ targetProjectId: project.id }, { itemsPerPage: 1 }).then((response) => {
      setProjectLinkCount(response.totalNumberOfItems);
      setProjectLinks(response.items);
    });
  }, [project, api, setDone]);

  const setStepStatus = useMemo(
    () => (task: 'explored-rest-api' | 'explored-graphql-api' | 'generated-client' | 'used-client' | 'ignore-clients', done: boolean) => {
      if (!project) {
        return;
      }

      if (done) {
        localStorage.setItem(`content-sync/onboarding/content-cloud/${project.id}/${task}`, 'true');
      } else {
        localStorage.removeItem(`content-sync/onboarding/content-cloud/${project.id}/${task}`);
      }

      if (task === 'explored-rest-api') {
        setExploredRestApi(done);
      } else if (task === 'explored-graphql-api') {
        setExploredGraphQlApi(done);
      } else if (task === 'generated-client') {
        setGeneratedClient(done);
      } else if (task === 'used-client') {
        setUsedClient(done);
      } else if (task === 'ignore-clients') {
        setIgnoreClients(done);
      }
    },
    [project]
  );

  const customClientStep = (
    <ProcessTaskStep
      index={3}
      last
      done={!!clientCount || !!ignoreClients}
      title="Custom authentication (optional)"
      description={
        <>
          <ProcessTaskStepDescription>Create a new client to sign custom JWTs with.</ProcessTaskStepDescription>
          <ProcessTaskStepDescription>If your environment is set to public, you can ignore this step.</ProcessTaskStepDescription>
        </>
      }
    >
      <div className="m-3">
        {clientCount === 0 && project && !ignoreClients ? (
          <>
            <ButtonLink to={`/projects/${project.id}/settings/clients`} variant="primary">
              Open
            </ButtonLink>{' '}
            <Button variant="light" onClick={() => setStepStatus('ignore-clients', true)}>
              Ignore
            </Button>
          </>
        ) : (
          <>
            <TaskStatusIcon done={clientCount} /> {clientCount !== null ? `${clientCount} clients` : null}
          </>
        )}
      </div>
    </ProcessTaskStep>
  );

  return (
    <>
      <ProcessTaskList
        activeTaskGroup={activeTaskGroup}
        onSelect={(taskGroup) => {
          setActiveTaskGroup(taskGroup);
          setHadUserInteraction(true);
        }}
      >
        <ProcessTask
          id={CONTENT_CLOUD_TASK_CREATE_SPACE}
          title={'Create a space'}
          hint="1 minute"
          status={taskStatus[CONTENT_CLOUD_TASK_CREATE_SPACE]}
        >
          <ProcessTaskStep
            index={1}
            last
            done={!!project}
            title="Create a space"
            description={
              <>
                <ProcessTaskStepDescription>Start a trial if you haven't yet.</ProcessTaskStepDescription>
              </>
            }
          >
            {project ? (
              <>
                <TaskStatusIcon done /> {project.name}{' '}
                <ButtonLink variant="link" to={`/projects/${project.id}/settings/overview`}>
                  view
                </ButtonLink>
              </>
            ) : (
              <ProjectFormNewCustomer
                forceNew={forceNew}
                onProject={(project) => {
                  setProject(project);

                  if (!project) {
                    CONTENT_CLOUD_ONBOARDING_TASK_NAMES.map((c) => setDone(c, false));
                    setActiveTaskGroup('create-space');
                  }
                }}
              />
            )}
          </ProcessTaskStep>
        </ProcessTask>

        <ProcessTask
          id={CONTENT_CLOUD_TASK_CONNECT_SOURCE}
          title="Connect a source"
          hint="4 minutes"
          status={taskStatus[CONTENT_CLOUD_TASK_CONNECT_SOURCE]}
        >
          <ProcessTaskStep
            index={1}
            done={!!projectLinkCount}
            title="Connect a source space"
            description={
              <>
                <ProcessTaskStepDescription>Connect a Drupal space to share content with this space.</ProcessTaskStepDescription>
              </>
            }
          >
            {projectLinkCount === 0 && project ? (
              <div className="p-3 rounded bg-lighter">
                {api && (
                  <AddProjectLinkForm
                    projectConnection="target"
                    project={project}
                    onSave={async (draft) => {
                      const entity = await api.syndication.projectLinks.create(draft);
                      setProjectLinks([entity]);
                      setProjectLinkCount(1);
                    }}
                  >
                    <Right>
                      <PrimaryActionButton type={'submit'}>Add source</PrimaryActionButton>
                    </Right>
                  </AddProjectLinkForm>
                )}
              </div>
            ) : (
              <div className="m-3">
                <TaskStatusIcon done={projectLinkCount} /> {projectLinks ? <span> {projectLinks.length} Sources connected.</span> : null}
              </div>
            )}
          </ProcessTaskStep>

          <ProcessTaskStep
            index={2}
            done={!!contentTypeCount}
            title="Create content model"
            description={
              <>
                <ProcessTaskStepDescription>
                  Wait for the content model to be created based on your Drupal content types. This may take a minute or two to synchronize.
                </ProcessTaskStepDescription>
                <ProcessTaskStepDescription>
                  If the content model isn't showing up, verify that you created one or more Flows in Drupal.
                </ProcessTaskStepDescription>
              </>
            }
          >
            <div className="m-3">
              <TaskStatusIcon done={contentTypeCount} />{' '}
              {contentTypeCount !== null ? <span>{contentTypeCount} content types available.</span> : null}
            </div>
          </ProcessTaskStep>

          <ProcessTaskStep
            index={3}
            last
            done={!!contentCount}
            title="Share content"
            description={
              <>
                <ProcessTaskStepDescription>Create and push content from your Drupal site.</ProcessTaskStepDescription>
                <ProcessTaskStepDescription>
                  If you are not familiar with the setup in Drupal, please follow the onboarding instructions of your Drupal space.
                </ProcessTaskStepDescription>
                <ProcessTaskStepDescription>
                  If content isn't showing up, verify that there's no typo in the pool machine names at the source link configuration.
                </ProcessTaskStepDescription>
              </>
            }
          >
            <div className="m-3">
              <TaskStatusIcon done={!!contentCount} /> {contentCount !== null ? <span>{contentCount} entries available.</span> : null}
            </div>
          </ProcessTaskStep>
        </ProcessTask>

        <ProcessTask
          id={CONTENT_CLOUD_TASK_INTEGRATE}
          title="Connect your frontend"
          hint="5 minutes"
          status={taskStatus[CONTENT_CLOUD_TASK_INTEGRATE]}
        >
          <ProcessTaskStep
            index={1}
            done={!!generatedClient}
            title="Generate client"
            description={
              <>
                <ProcessTaskStepDescription>Add the auto-generated client code to your frontend project.</ProcessTaskStepDescription>
                <ProcessTaskStepDescription>
                  If you are using a language we don't support yet, please continue with the{' '}
                  <Button variant="link" onClick={() => setActiveTaskGroup('explore')}>
                    Explore APIs
                  </Button>{' '}
                  step instead.
                </ProcessTaskStepDescription>
              </>
            }
          >
            <div className="m-3">
              {generatedClient === false ? (
                <>
                  <ButtonLink to="/content-cloud/client-generation" variant="primary">
                    Open
                  </ButtonLink>{' '}
                  <Button variant="light" onClick={() => setStepStatus('generated-client', true)}>
                    Mark as done
                  </Button>
                </>
              ) : (
                <>
                  <TaskStatusIcon done={generatedClient} />{' '}
                  <Button variant="light" onClick={() => setStepStatus('generated-client', false)}>
                    Undo
                  </Button>
                </>
              )}
            </div>
          </ProcessTaskStep>

          <ProcessTaskStep
            index={2}
            done={!!usedClient}
            title="Use client"
            description={
              <>
                <ProcessTaskStepDescription>Query for content in your app.</ProcessTaskStepDescription>
                <ProcessTaskStepDescription>
                  If the environment you want to use is not set to public, use the access token below for testing.
                </ProcessTaskStepDescription>
              </>
            }
          >
            <div className="m-3">
              {usedClient === false ? (
                <>
                  <ButtonLink to="/content-cloud/client-generation" variant="primary">
                    Open
                  </ButtonLink>{' '}
                  <Button variant="light" onClick={() => setStepStatus('used-client', true)}>
                    Mark as done
                  </Button>
                </>
              ) : (
                <>
                  <TaskStatusIcon done={usedClient} />{' '}
                  <Button variant="light" onClick={() => setStepStatus('used-client', false)}>
                    Undo
                  </Button>
                </>
              )}
            </div>
          </ProcessTaskStep>

          {customClientStep}
        </ProcessTask>

        <ProcessTask
          id={CONTENT_CLOUD_TASK_EXPLORE}
          title="Explore the APIs"
          hint="5 minutes"
          status={taskStatus[CONTENT_CLOUD_TASK_EXPLORE]}
        >
          <ProcessTaskStep
            index={1}
            done={!!exploredRestApi}
            title="Explore the REST API"
            description={
              <>
                <ProcessTaskStepDescription>Use the REST explorer to create custom API requests.</ProcessTaskStepDescription>
              </>
            }
          >
            <div className="m-3">
              {exploredRestApi === false ? (
                <>
                  <ButtonLink to="/content-cloud/rest-explorer" variant="primary">
                    Open
                  </ButtonLink>{' '}
                  <Button variant="light" onClick={() => setStepStatus('explored-rest-api', true)}>
                    Mark as done
                  </Button>
                </>
              ) : (
                <>
                  <TaskStatusIcon done={exploredRestApi} />{' '}
                  <Button variant="light" onClick={() => setStepStatus('explored-rest-api', false)}>
                    Undo
                  </Button>
                </>
              )}
            </div>
          </ProcessTaskStep>

          <ProcessTaskStep
            index={2}
            done={!!exploredGraphQlApi}
            title="Explore the GraphQL API"
            description={
              <>
                <ProcessTaskStepDescription>Use the GraphQL explorer to create custom queries.</ProcessTaskStepDescription>
              </>
            }
          >
            <div className="m-3">
              {exploredGraphQlApi === false ? (
                <>
                  <ButtonLink to="/content-cloud/graphql-explorer" variant="primary">
                    Open
                  </ButtonLink>{' '}
                  <Button variant="light" onClick={() => setStepStatus('explored-graphql-api', true)}>
                    Mark as done
                  </Button>
                </>
              ) : (
                <>
                  <TaskStatusIcon done={exploredGraphQlApi} />{' '}
                  <Button variant="light" onClick={() => setStepStatus('explored-graphql-api', false)}>
                    Undo
                  </Button>
                </>
              )}
            </div>
          </ProcessTaskStep>

          {customClientStep}
        </ProcessTask>

        <ProcessTask
          id={CONTENT_CLOUD_TASK_CONTENT_USER_DATA}
          title="Custom user data store"
          hint="5 minutes"
          status={taskStatus[CONTENT_CLOUD_TASK_CONTENT_USER_DATA]}
        >
          <ProcessTaskStep
            index={1}
            last
            done={!!contentUserDataTypeCount}
            title="Create custom user data store"
            description={
              <>
                <ProcessTaskStepDescription>
                  Create a custom user data store to save bookmarks, reading histories, notes and other data that help you customize the
                  user journey across your apps and services.
                </ProcessTaskStepDescription>
              </>
            }
          >
            <div className="m-3">
              <div>
                <TaskStatusIcon done={contentUserDataTypeCount} />{' '}
                {contentUserDataTypeCount !== null ? <span>{contentUserDataTypeCount} custom user data stores created.</span> : null}
              </div>
              <div className="mt-2">
                <PrimaryActionLinkButton to="/content-cloud/content-user-data" variant={contentUserDataTypeCount ? 'link' : 'primary'}>
                  Manage
                </PrimaryActionLinkButton>
              </div>
            </div>
          </ProcessTaskStep>
        </ProcessTask>

        <ProcessTask id={CONTENT_CLOUD_TASK_NEXT_STEPS} title="Next steps" status={taskStatus[CONTENT_CLOUD_TASK_NEXT_STEPS]}>
          <ProcessTaskStep
            index={1}
            done={false}
            title="Let us know what you think"
            description={
              <>
                <ProcessTaskStepDescription>
                  We would love to hear from you to learn what you love about our product and what we can do better.
                </ProcessTaskStepDescription>
              </>
            }
          >
            <PrimaryActionLinkButton to={FEEDBACK_URL} external>
              Share feedback
            </PrimaryActionLinkButton>
          </ProcessTaskStep>

          <ProcessTaskStep
            index={2}
            last
            done={false}
            title="Share with others"
            description={
              <>
                <ProcessTaskStepDescription>Help other Drupal users who don't know about this solution yet.</ProcessTaskStepDescription>
              </>
            }
          >
            Follow us on <FontAwesomeIcon icon={faLinkedin} />{' '}
            <ExternalLink to="https://www.linkedin.com/company/cms-content-sync/">LinkedIn</ExternalLink> and feel free to tag our page when
            sharing your experience. We would 😍 to hear from you!
          </ProcessTaskStep>
        </ProcessTask>
      </ProcessTaskList>
    </>
  );
}
